import { useEffect, useState } from "react";
import { Download, Share, PlusSquare, MoreVertical } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

let deferredPrompt = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferredPrompt = e; window.dispatchEvent(new Event("pwa-installable")); });
  window.addEventListener("appinstalled", () => { deferredPrompt = null; window.dispatchEvent(new Event("pwa-installed")); });
}

export const useInstallApp = () => {
  const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
  const [standalone, setStandalone] = useState(isStandalone());
  const [canPrompt, setCanPrompt] = useState(!!deferredPrompt);
  const [help, setHelp] = useState(false);
  useEffect(() => {
    const a = () => setCanPrompt(true);
    const b = () => { setStandalone(true); setCanPrompt(false); };
    window.addEventListener("pwa-installable", a);
    window.addEventListener("pwa-installed", b);
    return () => { window.removeEventListener("pwa-installable", a); window.removeEventListener("pwa-installed", b); };
  }, []);
  const install = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") deferredPrompt = null;
      setCanPrompt(!!deferredPrompt);
      return;
    }
    setHelp(true);
  };
  return { standalone, canPrompt, install, help, setHelp };
};

export const InstallHelpDialog = ({ open, onOpenChange }) => {
  const ua = navigator.userAgent;
  const ios = /iPhone|iPad|iPod/i.test(ua);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="install-help-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading inline-flex items-center gap-2"><Download size={18} className="text-emerald-600" /> Install Armella Budget as an app</DialogTitle>
          <DialogDescription>Installed, the tool opens full-screen from your home screen or desktop without browser bars.</DialogDescription>
        </DialogHeader>
        <ol className="space-y-3 text-sm text-slate-800">
          {ios ? (
            <>
              <li className="flex gap-3"><span className="font-bold text-emerald-700">1</span><span>Tap the <Share size={14} className="inline mx-1" /> <b>Share</b> button in Safari's toolbar.</span></li>
              <li className="flex gap-3"><span className="font-bold text-emerald-700">2</span><span>Choose <PlusSquare size={14} className="inline mx-1" /> <b>Add to Home Screen</b>.</span></li>
              <li className="flex gap-3"><span className="font-bold text-emerald-700">3</span><span>Tap <b>Add</b> — the Armella Budget icon appears on your home screen.</span></li>
            </>
          ) : (
            <>
              <li className="flex gap-3"><span className="font-bold text-emerald-700">1</span><span>Open the browser menu <MoreVertical size={14} className="inline mx-1" /> (Chrome / Edge) or the install icon in the address bar.</span></li>
              <li className="flex gap-3"><span className="font-bold text-emerald-700">2</span><span>Choose <b>Install app</b> / <b>Add to Home screen</b>.</span></li>
              <li className="flex gap-3"><span className="font-bold text-emerald-700">3</span><span>Confirm — Armella Budget opens as a stand-alone app.</span></li>
            </>
          )}
        </ol>
        <Button onClick={() => onOpenChange(false)} className="w-full bg-slate-900 hover:bg-emerald-700" data-testid="install-help-close">Got it</Button>
      </DialogContent>
    </Dialog>
  );
};
